/**
 * Sinais do onboarding: assinaturas de tarefa e detecção de marcos de ação
 * comparando um snapshot inicial (estado semeado pelo modelo) com o estado atual.
 *
 * Por que diff e não "existe alguma": o modelo já vem com subtarefas, dependências e
 * responsáveis preenchidos. Sem comparar com o snapshot, os itens do checklist
 * marcariam "feito" sozinhos no momento do seed.
 */
import type { Task } from '../types/database';

export type ChecklistKey =
  // Detectados por diff de tarefa ou por estado do projeto
  | 'create_project'
  | 'edit_task'
  | 'assign_owner'
  | 'add_subtask'
  | 'add_dependency'
  | 'view_critical_path'
  | 'move_kanban'
  | 'save_baseline'
  // Ações registradas pelos próprios módulos via evento (ver ONB_ACTION_EVENT)
  | 'add_member'
  | 'create_pendencia'
  | 'set_raci'
  | 'create_risk'
  | 'create_change'
  | 'create_cost'
  | 'view_portfolio';

/**
 * Evento no window que um módulo emite ao concluir uma ação de onboarding.
 * Mantém os módulos desacoplados do OnboardingContext (mesmo padrão do 'meugantt:cp-viewed').
 */
export const ONB_ACTION_EVENT = 'meugantt:onb-action';

export function notifyOnboardingAction(key: ChecklistKey): void {
  try {
    window.dispatchEvent(new CustomEvent(ONB_ACTION_EVENT, { detail: { key } }));
  } catch { /* ambiente sem window (SSR/teste) */ }
}

export interface TaskSig {
  name: string;
  start: string;
  end: string;
  assigned: string;
  deps: number;
  kanban: string;
  level: number;
}

function toISO(d: Date | string | null | undefined): string {
  if (!d) return '';
  const date = typeof d === 'string' ? new Date(d) : d;
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
}

interface FlatNode { task: Task; level: number; }

// Achata TODAS as tarefas, inclusive filhos de grupos colapsados (ao contrário de flattenTasks).
function flattenAll(tasks: Task[], level = 0, out: FlatNode[] = []): FlatNode[] {
  for (const t of tasks) {
    out.push({ task: t, level });
    if (t.children && t.children.length > 0) flattenAll(t.children, level + 1, out);
  }
  return out;
}

export function taskSignatures(tasks: Task[]): Map<string, TaskSig> {
  const map = new Map<string, TaskSig>();
  for (const { task, level } of flattenAll(tasks)) {
    map.set(task.id, {
      name: task.name || '',
      start: toISO(task.startDate),
      end: toISO(task.endDate),
      assigned: task.assigned || '',
      deps: task.dependencies?.length || 0,
      // `tasks.status`, e não `kanban_status`.
      //
      // O marco "mover um cartão no Kanban" observava o campo LEGADO, que o
      // quadro nunca escreveu: o item do checklist Primeiros Passos não
      // marcava quando a pessoa movia um cartão, só quando o progresso cruzava
      // 100 por outro caminho, porque era o `progressToKanbanStatus` (agora
      // removido) que escrevia ali. O e2e passava porque só conferia a troca
      // de coluna na tela, nunca o item do checklist.
      kanban: task.status || '',
      level,
    });
  }
  return map;
}

/** Serializa/deserializa o snapshot para localStorage. */
export function serializeSignatures(sig: Map<string, TaskSig>): string {
  return JSON.stringify(Array.from(sig.entries()));
}
export function deserializeSignatures(raw: string): Map<string, TaskSig> | null {
  try {
    return new Map(JSON.parse(raw) as Array<[string, TaskSig]>);
  } catch {
    return null;
  }
}

/**
 * Marcos de ação que dispararam comparando a base (snapshot) com o estado atual.
 * Retorna só os marcos baseados em diff de tarefa (os demais — projeto criado, baseline,
 * caminho crítico — são derivados de outras fontes no contexto).
 */
export function detectActionSignals(
  base: Map<string, TaskSig>,
  current: Map<string, TaskSig>,
): Partial<Record<ChecklistKey, boolean>> {
  let edited = false;
  let assigned = false;
  let kanban = false;
  let baseDeps = 0;
  let baseSub = 0;
  let curDeps = 0;
  let curSub = 0;

  for (const [, s] of base) {
    baseDeps += s.deps;
    if (s.level > 0) baseSub += 1;
  }
  for (const [id, cur] of current) {
    curDeps += cur.deps;
    if (cur.level > 0) curSub += 1;
    const prev = base.get(id);
    if (!prev) continue; // tarefa nova: contabilizada nas somas acima
    if (cur.name !== prev.name || cur.start !== prev.start || cur.end !== prev.end) edited = true;
    if (cur.assigned !== prev.assigned) assigned = true;
    if (cur.kanban !== prev.kanban) kanban = true;
  }

  return {
    edit_task: edited,
    assign_owner: assigned,
    add_dependency: curDeps > baseDeps,
    add_subtask: curSub > baseSub,
    move_kanban: kanban,
  };
}
